import { useState, useEffect } from 'react'
import AdminHeader from '../../components/admin/AdminHeader'
import AdminSidebar from '../../components/admin/AdminSidebar'
import RichTextEditor from '../../components/admin/RichTextEditor'
import { API_BASE_URL } from '../../config/api'

interface AboutSection {
  id?: number
  section: 'about' | 'vision' | 'mission'
  intro_content?: string
  hospital_journey_content?: string
  future_vision_content?: string
  founder_name?: string
  founder_message?: string
  title?: string
  content?: string
  is_active?: number
  created_at?: string
  updated_at?: string
}

const AboutUsManagement = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [sections, setSections] = useState<AboutSection[]>([])
  const [loading, setLoading] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [showViewModal, setShowViewModal] = useState(false)
  const [viewingSection, setViewingSection] = useState<AboutSection | null>(null)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [saving, setSaving] = useState(false)

  const [formData, setFormData] = useState<AboutSection>({
    section: 'about',
    intro_content: '',
    hospital_journey_content: '',
    future_vision_content: '',
    founder_name: '',
    founder_message: '',
    title: '',
    content: '',
    is_active: 1
  })


  // Fetch all sections
  const fetchAllSections = async () => {
    setLoading(true)
    setError('')
    try {
      const response = await fetch(`${API_BASE_URL}about_us_list.php`)
      const result = await response.json()
      
      if (result.status && result.data) {
        // Handle both array and object response formats
        if (Array.isArray(result.data)) {
          setSections(result.data)
        } else {
          // Convert object to array
          const sectionsArray = Object.values(result.data) as AboutSection[]
          setSections(sectionsArray)
        }
      } else {
        setError(result.message || 'Failed to fetch sections')
      }
    } catch (err) {
      setError('Failed to fetch sections. Please try again.')
      console.error('Error fetching sections:', err)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchAllSections()
  }, [])

  // Handle update/create
  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!formData.section) return

    setError('')
    setSuccess('')
    setSaving(true)

    try {
      const formDataToSend = new FormData()
      
      // Add section type
      formDataToSend.append('section', formData.section)
      
      // Add ID if editing
      if (formData.id) {
        formDataToSend.append('id', formData.id.toString())
      }

      // Add fields based on section type
      if (formData.section === 'about') {
        formDataToSend.append('intro_content', formData.intro_content || '')
        formDataToSend.append('hospital_journey_content', formData.hospital_journey_content || '')
        formDataToSend.append('future_vision_content', formData.future_vision_content || '')
        formDataToSend.append('founder_name', formData.founder_name || '')
        formDataToSend.append('founder_message', formData.founder_message || '')
      } else if (formData.section === 'vision' || formData.section === 'mission') {
        formDataToSend.append('title', formData.title || '')
        formDataToSend.append('content', formData.content || '')
      }

      formDataToSend.append('is_active', (formData.is_active || 1).toString())

      const response = await fetch(`${API_BASE_URL}about_us_update.php`, {
        method: 'POST',
        body: formDataToSend
      })

      const result = await response.json()

      if (result.status) {
        setSuccess(`${formData.section.charAt(0).toUpperCase() + formData.section.slice(1)} section updated successfully!`)
        setShowEditModal(false)
        resetForm()
        fetchAllSections()
        
        setTimeout(() => setSuccess(''), 3000)
      } else {
        setError(result.message || 'Failed to update section')
      }
    } catch (err) {
      setError('Failed to update section. Please try again.')
      console.error('Error updating section:', err)
    } finally {
      setSaving(false)
    }
  }

  // Handle delete
  const handleDelete = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this section? This action cannot be undone.')) {
      return
    }

    setError('')
    setSuccess('')

    try {
      const response = await fetch(`${API_BASE_URL}about_us_delete.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ id })
      })

      const result = await response.json()

      if (result.status) {
        setSuccess('Section deleted successfully!')
        fetchAllSections()
        setTimeout(() => setSuccess(''), 3000)
      } else {
        setError(result.message || 'Failed to delete section')
      }
    } catch (err) {
      setError('Failed to delete section. Please try again.')
      console.error('Error deleting section:', err)
    }
  }

  // Open edit modal
  const openEditModal = (section: AboutSection) => {
    setFormData({
      id: section.id,
      section: section.section,
      intro_content: section.intro_content || '',
      hospital_journey_content: section.hospital_journey_content || '',
      future_vision_content: section.future_vision_content || '',
      founder_name: section.founder_name || '',
      founder_message: section.founder_message || '',
      title: section.title || '',
      content: section.content || '',
      is_active: section.is_active || 1
    })
    setShowEditModal(true)
  }

  // Open create modal
  const openCreateModal = (sectionType: 'about' | 'vision' | 'mission') => {
    resetForm()
    setFormData({
      section: sectionType,
      intro_content: '',
      hospital_journey_content: '',
      future_vision_content: '',
      founder_name: '',
      founder_message: '',
      title: sectionType === 'vision' ? 'Our Vision' : sectionType === 'mission' ? 'Our Mission' : '',
      content: '',
      is_active: 1
    })
    setShowEditModal(true)
  }

  // Open view modal
  const openViewModal = (section: AboutSection) => {
    setViewingSection(section)
    setShowViewModal(true)
  }

  // Reset form
  const resetForm = () => {
    setFormData({
      section: 'about',
      intro_content: '',
      hospital_journey_content: '',
      future_vision_content: '',
      founder_name: '',
      founder_message: '',
      title: '',
      content: '',
      is_active: 1
    })
    setError('')
  }

  // Get section by type
  const getSectionByType = (type: 'about' | 'vision' | 'mission') => {
    return sections.find(s => s.section === type)
  }

  const aboutSection = getSectionByType('about')
  const visionSection = getSectionByType('vision')
  const missionSection = getSectionByType('mission')

  return (
    <div className="min-h-screen bg-gray-900">
      <AdminHeader onMenuToggle={() => setSidebarOpen(!sidebarOpen)} />
      <div className="flex">
        <AdminSidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
        <main className="flex-1 md:ml-64 p-4 md:p-6">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center justify-between mb-6">
              <h1 className="text-3xl font-bold text-white">About Us Management</h1>
            </div>

            {/* Success/Error Messages */}
            {success && (
              <div className="mb-4 p-4 bg-green-500/20 border border-green-500 rounded-lg animate-fadeIn">
                <p className="text-green-400">{success}</p>
              </div>
            )}
            {error && (
              <div className="mb-4 p-4 bg-red-500/20 border border-red-500 rounded-lg animate-fadeIn">
                <p className="text-red-400">{error}</p>
              </div>
            )}

            {/* Sections Grid */}
            {loading ? (
              <div className="bg-gray-800 rounded-lg p-12 border border-gray-700 text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-light mx-auto mb-4"></div>
                <p className="text-gray-400 text-lg">Loading sections...</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {/* About Section Card */}
                <div className="bg-gray-800 rounded-lg border border-gray-700 overflow-hidden">
                  <div className="p-6">
                    <div className="flex items-center justify-between mb-4">
                      <h2 className="text-2xl font-bold text-white">About Us</h2>
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                        aboutSection?.is_active === 1
                          ? 'bg-green-500 text-white'
                          : 'bg-gray-600 text-gray-300'
                      }`}>
                        {aboutSection?.is_active === 1 ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                    {aboutSection ? (
                      <>
                        {aboutSection.founder_name && (
                          <div className="mb-3">
                            <p className="text-primary-light font-semibold text-sm mb-1">Founder</p>
                            <p className="text-white text-base">{aboutSection.founder_name}</p>
                          </div>
                        )}
                        {aboutSection.intro_content && (
                          <div className="mb-3">
                            <p className="text-gray-400 text-xs mb-1">Preview</p>
                            <p className="text-gray-300 text-sm line-clamp-3">
                              {aboutSection.intro_content.substring(0, 150)}...
                            </p>
                          </div>
                        )}
                      </>
                    ) : (
                      <p className="text-gray-400 text-sm mb-4">
                        Manage the main About Us content including introduction, hospital journey, future vision, and founder message.
                      </p>
                    )}
                    <div className="flex space-x-2 mt-4">
                      {aboutSection && (
                        <>
                          <button
                            onClick={() => openViewModal(aboutSection)}
                            className="flex-1 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg font-semibold transition-colors text-sm"
                          >
                            View
                          </button>
                          <button
                            onClick={() => openEditModal(aboutSection)}
                            className="flex-1 px-4 py-2 bg-primary-light hover:bg-primary-dark text-white rounded-lg font-semibold transition-colors text-sm"
                          >
                            Edit
                          </button>
                        </>
                      )}
                      {!aboutSection && (
                        <button
                          onClick={() => openCreateModal('about')}
                          className="w-full px-4 py-2 bg-primary-light hover:bg-primary-dark text-white rounded-lg font-semibold transition-colors text-sm"
                        >
                          Create
                        </button>
                      )}
                    </div>
                  </div>
                </div>

                {/* Vision Section Card */}
                <div className="bg-gray-800 rounded-lg border border-gray-700 overflow-hidden">
                  <div className="p-6">
                    <div className="flex items-center justify-between mb-4">
                      <h2 className="text-2xl font-bold text-white">Vision</h2>
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                        visionSection?.is_active === 1
                          ? 'bg-green-500 text-white'
                          : 'bg-gray-600 text-gray-300'
                      }`}>
                        {visionSection?.is_active === 1 ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                    {visionSection ? (
                      <>
                        {visionSection.title && (
                          <div className="mb-3">
                            <p className="text-primary-light font-semibold text-sm mb-1">Title</p>
                            <p className="text-white text-base">{visionSection.title}</p>
                          </div>
                        )}
                        {visionSection.content && (
                          <div className="mb-3">
                            <p className="text-gray-400 text-xs mb-1">Preview</p>
                            <p className="text-gray-300 text-sm line-clamp-3">
                              {visionSection.content.substring(0, 150)}...
                            </p>
                          </div>
                        )}
                      </>
                    ) : (
                      <p className="text-gray-400 text-sm mb-4">
                        Manage the vision statement that describes the organization's long-term goals and aspirations.
                      </p>
                    )}
                    <div className="flex space-x-2 mt-4">
                      {visionSection && (
                        <>
                          <button
                            onClick={() => openViewModal(visionSection)}
                            className="flex-1 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg font-semibold transition-colors text-sm"
                          >
                            View
                          </button>
                          <button
                            onClick={() => openEditModal(visionSection)}
                            className="flex-1 px-4 py-2 bg-primary-light hover:bg-primary-dark text-white rounded-lg font-semibold transition-colors text-sm"
                          >
                            Edit
                          </button>
                        </>
                      )}
                      {!visionSection && (
                        <button
                          onClick={() => openCreateModal('vision')}
                          className="w-full px-4 py-2 bg-primary-light hover:bg-primary-dark text-white rounded-lg font-semibold transition-colors text-sm"
                        >
                          Create
                        </button>
                      )}
                    </div>
                  </div>
                </div>

                {/* Mission Section Card */}
                <div className="bg-gray-800 rounded-lg border border-gray-700 overflow-hidden">
                  <div className="p-6">
                    <div className="flex items-center justify-between mb-4">
                      <h2 className="text-2xl font-bold text-white">Mission</h2>
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                        missionSection?.is_active === 1
                          ? 'bg-green-500 text-white'
                          : 'bg-gray-600 text-gray-300'
                      }`}>
                        {missionSection?.is_active === 1 ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                    {missionSection ? (
                      <>
                        {missionSection.title && (
                          <div className="mb-3">
                            <p className="text-primary-light font-semibold text-sm mb-1">Title</p>
                            <p className="text-white text-base">{missionSection.title}</p>
                          </div>
                        )}
                        {missionSection.content && (
                          <div className="mb-3">
                            <p className="text-gray-400 text-xs mb-1">Preview</p>
                            <p className="text-gray-300 text-sm line-clamp-3">
                              {missionSection.content.substring(0, 150)}...
                            </p>
                          </div>
                        )}
                      </>
                    ) : (
                      <p className="text-gray-400 text-sm mb-4">
                        Manage the mission statement that describes the organization's purpose and core values.
                      </p>
                    )}
                    <div className="flex space-x-2 mt-4">
                      {missionSection && (
                        <>
                          <button
                            onClick={() => openViewModal(missionSection)}
                            className="flex-1 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg font-semibold transition-colors text-sm"
                          >
                            View
                          </button>
                          <button
                            onClick={() => openEditModal(missionSection)}
                            className="flex-1 px-4 py-2 bg-primary-light hover:bg-primary-dark text-white rounded-lg font-semibold transition-colors text-sm"
                          >
                            Edit
                          </button>
                        </>
                      )}
                      {!missionSection && (
                        <button
                          onClick={() => openCreateModal('mission')}
                          className="w-full px-4 py-2 bg-primary-light hover:bg-primary-dark text-white rounded-lg font-semibold transition-colors text-sm"
                        >
                          Create
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </main>
      </div>

      {/* Edit/Create Modal */}
      {showEditModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm p-4">
          <div className="bg-gray-800 rounded-2xl shadow-2xl p-8 max-w-4xl w-full border border-gray-700 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-white">
                {formData.id ? 'Edit' : 'Create'} {formData.section.charAt(0).toUpperCase() + formData.section.slice(1)} Section
              </h2>
              <button
                onClick={() => {
                  setShowEditModal(false)
                  resetForm()
                }}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <form onSubmit={handleUpdate} className="space-y-6">
              {formData.section === 'about' ? (
                <>
                  <div>
                    <label htmlFor="intro_content" className="block text-white font-semibold mb-2">
                      Introduction Content *
                    </label>
                    <RichTextEditor
                      value={formData.intro_content || ''}
                      onChange={(value) => setFormData({ ...formData, intro_content: value })}
                      placeholder="Enter introduction content..."
                      rows={6}
                    />
                  </div>

                  <div>
                    <label htmlFor="hospital_journey_content" className="block text-white font-semibold mb-2">
                      Hospital Journey Content *
                    </label>
                    <RichTextEditor
                      value={formData.hospital_journey_content || ''}
                      onChange={(value) => setFormData({ ...formData, hospital_journey_content: value })}
                      placeholder="Enter hospital journey content..."
                      rows={8}
                    />
                  </div>

                  <div>
                    <label htmlFor="future_vision_content" className="block text-white font-semibold mb-2">
                      Future Vision Content *
                    </label>
                    <RichTextEditor
                      value={formData.future_vision_content || ''}
                      onChange={(value) => setFormData({ ...formData, future_vision_content: value })}
                      placeholder="Enter future vision content..."
                      rows={6}
                    />
                  </div>

                  <div>
                    <label htmlFor="founder_name" className="block text-white font-semibold mb-2">
                      Founder Name *
                    </label>
                    <input
                      type="text"
                      id="founder_name"
                      value={formData.founder_name}
                      onChange={(e) => setFormData({ ...formData, founder_name: e.target.value })}
                      className="w-full px-4 py-3 rounded-lg bg-gray-700 text-white placeholder-gray-400 border-2 border-gray-600 focus:outline-none focus:border-primary-light"
                      placeholder="Enter founder name"
                      required
                    />
                  </div>

                  <div>
                    <label htmlFor="founder_message" className="block text-white font-semibold mb-2">
                      Founder Message *
                    </label>
                    <RichTextEditor
                      value={formData.founder_message || ''}
                      onChange={(value) => setFormData({ ...formData, founder_message: value })}
                      placeholder="Enter founder message..."
                      rows={10}
                    />
                  </div>
                </>
              ) : (
                <>
                  <div>
                    <label htmlFor="title" className="block text-white font-semibold mb-2">
                      Title *
                    </label>
                    <input
                      type="text"
                      id="title"
                      value={formData.title}
                      onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                      className="w-full px-4 py-3 rounded-lg bg-gray-700 text-white placeholder-gray-400 border-2 border-gray-600 focus:outline-none focus:border-primary-light"
                      placeholder={`Enter ${formData.section} title`}
                      required
                    />
                  </div>

                  <div>
                    <label htmlFor="content" className="block text-white font-semibold mb-2">
                      Content *
                    </label>
                    <RichTextEditor
                      value={formData.content || ''}
                      onChange={(value) => setFormData({ ...formData, content: value })}
                      placeholder={`Enter ${formData.section} content...`}
                      rows={12}
                    />
                  </div>
                </>
              )}

              <div>
                <label htmlFor="is_active" className="block text-white font-semibold mb-2">
                  Active Status *
                </label>
                <select
                  id="is_active"
                  value={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: parseInt(e.target.value) })}
                  className="w-full px-4 py-3 rounded-lg bg-gray-700 text-white border-2 border-gray-600 focus:outline-none focus:border-primary-light"
                  required
                >
                  <option value={1}>Active</option>
                  <option value={0}>Inactive</option>
                </select>
              </div>

              <div className="flex space-x-4 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowEditModal(false)
                    resetForm()
                  }}
                  className="flex-1 px-6 py-3 bg-gray-700 hover:bg-gray-600 text-white rounded-lg font-semibold transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className={`flex-1 px-6 py-3 rounded-lg font-semibold transition-colors ${
                    saving
                      ? 'bg-gray-600 cursor-not-allowed text-gray-400'
                      : 'bg-primary-light hover:bg-primary-dark text-white'
                  }`}
                >
                  {saving ? 'Saving...' : formData.id ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* View Modal */}
      {showViewModal && viewingSection && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm p-4">
          <div className="bg-gray-800 rounded-2xl shadow-2xl p-8 max-w-4xl w-full border border-gray-700 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-white">
                {viewingSection.section.charAt(0).toUpperCase() + viewingSection.section.slice(1)} Section
              </h2>
              <button
                onClick={() => {
                  setShowViewModal(false)
                  setViewingSection(null)
                }}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="space-y-6">
              {viewingSection.section === 'about' ? (
                <>
                  <div>
                    <label className="block text-gray-400 text-sm mb-2">Introduction Content</label>
                    <div className="bg-gray-700 rounded-lg p-4 text-white whitespace-pre-wrap">
                      {viewingSection.intro_content}
                    </div>
                  </div>

                  <div>
                    <label className="block text-gray-400 text-sm mb-2">Hospital Journey Content</label>
                    <div className="bg-gray-700 rounded-lg p-4 text-white whitespace-pre-wrap">
                      {viewingSection.hospital_journey_content}
                    </div>
                  </div>

                  <div>
                    <label className="block text-gray-400 text-sm mb-2">Future Vision Content</label>
                    <div className="bg-gray-700 rounded-lg p-4 text-white whitespace-pre-wrap">
                      {viewingSection.future_vision_content}
                    </div>
                  </div>

                  <div>
                    <label className="block text-gray-400 text-sm mb-2">Founder Name</label>
                    <p className="text-white font-semibold">{viewingSection.founder_name}</p>
                  </div>

                  <div>
                    <label className="block text-gray-400 text-sm mb-2">Founder Message</label>
                    <div className="bg-gray-700 rounded-lg p-4 text-white" dangerouslySetInnerHTML={{ __html: viewingSection.founder_message || '' }} />
                  </div>
                </>
              ) : (
                <>
                  <div>
                    <label className="block text-gray-400 text-sm mb-2">Title</label>
                    <p className="text-white font-bold text-xl">{viewingSection.title}</p>
                  </div>

                  <div>
                    <label className="block text-gray-400 text-sm mb-2">Content</label>
                    <div className="bg-gray-700 rounded-lg p-4 text-white" dangerouslySetInnerHTML={{ __html: viewingSection.content || '' }} />
                  </div>
                </>
              )}

              <div className="flex items-center justify-between pt-4 border-t border-gray-700">
                <div>
                  <label className="block text-gray-400 text-sm mb-2">Active Status</label>
                  <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold ${
                    (viewingSection.is_active ?? 1) === 1
                      ? 'bg-green-500 text-white'
                      : 'bg-gray-600 text-gray-300'
                  }`}>
                    {(viewingSection.is_active ?? 1) === 1 ? 'Active' : 'Inactive'}
                  </span>
                </div>
                {viewingSection.id && (
                  <button
                    onClick={() => handleDelete(viewingSection.id!)}
                    className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-semibold transition-colors"
                  >
                    Delete
                  </button>
                )}
              </div>

              <div className="flex space-x-4 pt-4">
                <button
                  onClick={() => {
                    setShowViewModal(false)
                    openEditModal(viewingSection)
                  }}
                  className="flex-1 px-6 py-3 bg-primary-light hover:bg-primary-dark text-white rounded-lg font-semibold transition-colors"
                >
                  Edit Section
                </button>
                <button
                  onClick={() => {
                    setShowViewModal(false)
                    setViewingSection(null)
                  }}
                  className="flex-1 px-6 py-3 bg-gray-700 hover:bg-gray-600 text-white rounded-lg font-semibold transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default AboutUsManagement

